import { useEffect } from 'react';
import { ArrowRightIcon, InfoIcon, PhoneIcon } from 'lucide-react';
import { business, deliveryDockAlt, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Hot Tub & Backyard Living Blog | Pegasus Spas Brooklyn, MI';
const DESCRIPTION = 'Read practical hot tub care, seasonal ownership and backyard-living articles from Pegasus Spas, serving Brooklyn, Michigan and surrounding areas since 1977.';
const CANONICAL = 'https://www.pegasus-spas.com/16.html';
const topics = [{
  label: 'All Articles',
  href: '#latest'
}, {
  label: 'Spa Care',
  href: '#featured'
}, {
  label: 'Service',
  href: '#featured'
}, {
  label: 'Backyard Living',
  href: '#latest'
}, {
  label: 'Pegasus News',
  href: '#archive'
}];
const winterPoints = ['Leave the spa filled and running; do not disconnect power.', 'Keep the cover closed.', 'Do not drain the spa unless the plumbing can be properly winterized.', 'Do not pour antifreeze into the tub.', 'Remove snow from the cover with a broom, never a shovel.'];
type Post = {
  title: string;
  date: string;
  category: string;
  excerpt: string;
  legacyUrl?: string;
  image: string;
  alt: string;
};
const posts: Post[] = [{
  title: 'Thank You',
  date: 'December 16, 2024',
  category: 'Pegasus News',
  excerpt: 'A year-end note of gratitude to the customers and families who continue to support Pegasus Spas.',
  legacyUrl: 'https://www.pegasus-spas.com/16.html?m11:post=thank-you',
  image: images.deliveryDock,
  alt: deliveryDockAlt
}, {
  title: 'Winter Tips for Hot Tub Care',
  date: 'February 1, 2022',
  category: 'Spa Care',
  excerpt: 'A practical seasonal checklist covering filters, circulation, water level, cover care and regular winter use.',
  legacyUrl: 'https://www.pegasus-spas.com/16.html?m11:post=winter-tips-for-hot-tub-care',
  image: images.pathService,
  alt: 'Spa technician inspecting plumbing inside an open hot tub equipment bay'
}];
type ArchiveEntry = {
  title: string;
  date: string;
  category: string;
  historical?: boolean;
};
const archive: ArchiveEntry[] = [{
  title: 'Hello Summer, Memorial Day Sale',
  date: 'May 19, 2023',
  category: 'Archived Promotion',
  historical: true
}, {
  title: 'Make the Summer Endless & Embrace the Cool Nights!',
  date: 'August 25, 2022',
  category: 'Backyard Living'
}, {
  title: 'Hot Tubs in Stock & Ready for Delivery!!',
  date: 'March 29, 2022',
  category: 'Archived Inventory Update',
  historical: true
}, {
  title: 'Just Arrived! 6 Sundance Spas Ready for Delivery!',
  date: 'January 20, 2022',
  category: 'Archived Inventory Update',
  historical: true
}, {
  title: 'Only 2 Months Till Christmas! Are You Ready?',
  date: 'October 26, 2020',
  category: 'Archived Seasonal Note'
}, {
  title: 'Pegasus Re-Opening May 18th @10AM',
  date: 'May 16, 2020',
  category: 'Archived Company Update'
}];
const browseTopics = [{
  label: 'Spa Care & FAQs',
  body: 'Water care, filters and the questions new owners ask most.',
  href: routes.spaCare
}, {
  label: 'Service & Repair',
  body: 'How we help when something is not working right.',
  href: routes.service
}, {
  label: 'Hot Tubs',
  body: 'Sundance, Viking and certified pre-owned paths.',
  href: routes.hotTubs
}, {
  label: 'Backyard Living',
  body: 'Furniture, grills, fire tables and shade.',
  href: routes.furniture
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function BlogPage() {
  useEffect(() => {
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) target.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-801" data-mp="1hqq24p40h89z">
      {/* Compact interior hero */}
      <section aria-labelledby="blog-h1" className="w-full bg-water" data-id="element-802" data-mp="1lt2v0cxntbpc">
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-803" data-mp="1ql4g8oct2cri">
          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-12 lg:items-start lg:gap-12" data-id="element-804" data-mp="1qn73x1qhjtt2">
            <div className="lg:col-span-7" data-id="element-805" data-mp="1vls5p5ldyljm">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-806" data-mp="20jtqbuygmsx7">
                Pegasus Spas Journal
              </p>
              <h1 id="blog-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-807" data-mp="25id95mhrbolc">
                Hot Tub &amp; Backyard Living Advice from Pegasus Spas
              </h1>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-808" data-mp="3g16pr6jargva">
                Practical notes for Michigan hot tub owners, backyard cooks and anyone planning more
                time outdoors — shared by our team.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-809" data-mp="ckhvok0whzmi">
                <SiteLink to={routes.spaCare} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-810" data-mp="7nhlpy0xkcq4">
                  Spa Care &amp; FAQs
                </SiteLink>
                <SiteLink to={routes.specials} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-811" data-mp="qkiwjzh6171z">
                  Current Specials
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-812" data-mp="2g14m1stqyph5">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-813" data-mp="2g03cjssdqb1j">
                <img src={images.pathHotTubs} alt="Hot tub on a residential deck beside a Michigan home" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-814" data-mp="2213keb5nre0m" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Topic links */}
      <section aria-label="Article topics" className="w-full border-y border-ink/10 bg-white" data-id="element-815" data-mp="3mj1fjlqpjubn">
        <div className="mx-auto max-w-container px-5 py-4 sm:px-8" data-id="element-816" data-mp="3rgjt5pfo6xpz">
          <ul className="flex flex-col gap-x-7 gap-y-0 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-817" data-mp="1fv1686l9drxm">
            {topics.map(topic => <li key={topic.label} data-id="element-818" data-mp="1assa51d3a9ra">
                <a href={topic.href} className="inline-flex min-h-[44px] items-center text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-819" data-mp="1pkaxjk8a36ko">
                  {topic.label}
                </a>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Featured guide */}
      <section id="featured" aria-labelledby="featured-heading" className="w-full scroll-mt-[120px] bg-white" data-id="element-820" data-mp="23jtv5j3gqziq">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-821" data-mp="e55tabsi0zyy">
          <article className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-822" data-mp="96ksob9c4e9z">
            <div className="lg:col-span-7" data-id="element-823" data-mp="3ji9onefvw0l9">
              <p className="text-[13px] font-bold uppercase tracking-normal text-pegasus-blue" data-id="element-824" data-mp="3jhhpjtn2vkq8">
                Spa Care &amp; Service
              </p>
              <p className="mt-1.5 text-[15px] text-ink/60" data-id="element-825" data-mp="3t71bj8637upb">
                <time dateTime="2024-01-12" data-id="element-826" data-mp="3t9nppqnhh8gi">January 12, 2024</time>
              </p>
              <h2 id="featured-heading" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-827" data-mp="20gn7w7f30vz">
                Winter Storm Tips
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-828" data-mp="35k2yyll396wo">
                What to do if your hot tub stops working or your home loses power during freezing
                weather.
              </p>

              <ul className="mt-5 space-y-2 border-t border-ink/15 pt-5" data-id="element-829" data-mp="pquh7s9kb9t7">
                {winterPoints.map(point => <li key={point} className="text-[16.5px] leading-6 text-ink/85" data-id="element-830" data-mp="tro6aoyqquno">
                    {point}
                  </li>)}
              </ul>

              <div className="mt-6 flex max-w-2xl items-start gap-3 rounded-card border border-ink/20 bg-water p-4" data-id="element-831" data-mp="2a8v8frqgcimk">
                <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-832" data-mp="25hmz0vp4gx9e" />
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-833" data-mp="20itlj39u5698">
                  Freezing-weather situations can vary. If you are unsure how to protect your spa,
                  call us for guidance.
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-834" data-mp="1misjhkhu3ss6">
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-835" data-mp="1ml5a0m1nrbgu">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-836" data-mp="3bwe83moefoei" />
                  Call or Text {business.phone}
                </a>
                <SiteLink to={routes.service} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-837" data-mp="v758uxp819n4">
                  Spa Service &amp; Repair
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-838" data-mp="ch5o2n0eygpi">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-839" data-mp="q795n0oclm8m">
                <img src={images.pathCovers} alt="A fitted spa cover closed over a hot tub" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-840" data-mp="q9c7qj2rz5tp" />
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Latest posts */}
      <section id="latest" aria-labelledby="latest-heading" className="w-full scroll-mt-[120px] border-y border-ink/10 bg-white" data-id="element-841" data-mp="24d4799tlh4gp">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-842" data-mp="1vflgdot9rvww">
          <h2 id="latest-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-843" data-mp="20d3v5vtyx5ct">
            From Our Blog
          </h2>

          <div className="mt-8 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2" data-id="element-844" data-mp="2s4t2oz1pq91v">
            {posts.map(post => <article key={post.title} className="flex flex-col overflow-hidden rounded-card border border-ink/15 bg-white" data-id="element-845" data-mp="2e5jpm88szv24">
                <div className="aspect-[3/2] w-full overflow-hidden" data-id="element-846" data-mp="12pv1venq6s09">
                  <img src={post.image} alt={post.alt} className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-847" data-mp="12ojwxbijne7k" />
                </div>
                <div className="flex flex-1 flex-col p-4" data-id="element-848" data-mp="jmjen5vgturk">
                  <p className="text-[13px] font-bold uppercase text-pegasus-blue" data-id="element-849" data-mp="eu9vq9srpuys">
                    {post.category}
                  </p>
                  <h3 className="mt-1.5 text-[19px] font-semibold leading-7 text-ink" data-id="element-850" data-mp="9une7o5wpsk6">
                    {post.title}
                  </h3>
                  <p className="mt-1 text-[15px] text-ink/60" data-id="element-851" data-mp="3w2n1wzley0oj">{post.date}</p>
                  <p className="mt-2 text-[16px] leading-6 text-ink/80" data-id="element-852" data-mp="3w4rgqckuok3l">{post.excerpt}</p>

                  <div className="mt-auto pt-4" data-id="element-853" data-mp="3s1bmto060xul">
                    {post.legacyUrl ? <a href={post.legacyUrl} className="inline-flex min-h-[44px] items-center gap-1.5 text-[15.5px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-854" data-mp="3n3jj7wq5rnak">
                        View archived post
                        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-855" data-mp="1xr4r5fbu7f55" />
                      </a> : <p className="text-[15.5px] text-ink/60" data-id="element-856" data-mp="27ir8zuiokcgv">Archived post</p>}
                  </div>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      {/* From the archive */}
      <section id="archive" aria-labelledby="archive-heading" className="w-full scroll-mt-[120px] bg-white" data-id="element-857" data-mp="1eulptc79yvj5">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-858" data-mp="1nurx2gcdqz9v">
          <h2 id="archive-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-859" data-mp="1nsp35l83wlzv">
            From the Archive
          </h2>
          <p className="mt-4 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-860" data-mp="2zc9u6wzgh0nf">
            Older posts preserve a snapshot of past Pegasus news and promotions. Dates are shown so
            readers can distinguish historical announcements from current offers.
          </p>

          <ul className="mt-7 max-w-4xl border-t border-ink/20" data-id="element-861" data-mp="37ssdehwimtro">
            {archive.map(entry => <li key={entry.title} className="flex flex-col gap-1 border-b border-ink/15 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-862" data-mp="32qjkfljn7rpk">
                <div data-id="element-863" data-mp="3bsj0aaix8pqx">
                  <h3 className="text-[17.5px] font-semibold leading-7 text-ink" data-id="element-864" data-mp="3bqyepael4h57">{entry.title}</h3>
                  <p className="mt-0.5 text-[15.5px] text-ink/65" data-id="element-865" data-mp="3qjji365k5vzq">
                    {entry.category} · {entry.date}
                  </p>
                </div>
                {entry.historical && <p className="shrink-0 text-[14px] font-medium text-ink/55" data-id="element-866" data-mp="3vgs2zei3ce4o">
                    Historical — not a current offer
                  </p>}
              </li>)}
          </ul>

          <SiteLink to={routes.specials} className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-867" data-mp="myj45lf4pdmf">
            See today's current specials
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-868" data-mp="3vcrpq9j8h453" />
          </SiteLink>
        </div>
      </section>

      {/* Browse by topic */}
      <section aria-labelledby="browse-heading" className="w-full bg-water" data-id="element-869" data-mp="3mexruu5tm2ft">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-870" data-mp="3qezwnc6urbzb">
          <h2 id="browse-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-871" data-mp="3cfqdc3nco1r4">
            Browse by Topic
          </h2>

          <ul className="mt-7 max-w-4xl border-t border-ink/20" data-id="element-872" data-mp="3hgnwaig6ozto">
            {browseTopics.map(topic => <li key={topic.label} className="flex flex-col gap-1 border-b border-ink/15 py-4 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-873" data-mp="1i7wcv9tsipwo">
                <div data-id="element-874" data-mp="1976nanv4hvre">
                  <h3 className="text-[17.5px] font-semibold leading-7 text-ink" data-id="element-875" data-mp="197zj4tsjn0ur">{topic.label}</h3>
                  <p className="mt-0.5 text-[16px] leading-6 text-ink/75" data-id="element-876" data-mp="25xqpbqa76cge">{topic.body}</p>
                </div>
                <SiteLink to={topic.href} className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-877" data-mp="21wx0muoxktmg">
                  {topic.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-878" data-mp="ghrb3urq1wtw" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Final navy CTA */}
      <section aria-labelledby="answer-heading" className="w-full bg-ink" data-id="element-879" data-mp="be6xlaiy3tte">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-880" data-mp="gciaxv8g8mgd">
          <div className="max-w-3xl" data-id="element-881" data-mp="l38fj691wla0">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-882" data-mp="l49xluohi818">Need a Local Answer?</p>
            <h2 id="answer-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-883" data-mp="p4aern2ywxbz">
              Talk With the Pegasus Spas Team
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-884" data-mp="z4183lfsl6ah">
              Call, text or stop by the Brooklyn showroom when an article leaves you with a question
              about your own spa or backyard.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-885" data-mp="35t7i107bu258">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-886" data-mp="35vk6zxbi5crs">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-887" data-mp="1ldriiwx0tsvw" />
              Call or Text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-888" data-mp="smznw3vltcao">
              Visit the Showroom
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}